import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ServicesLanding.css';

const ServiceCard = ({ icon, title, subtitle, onClick, primary, isDarkMode }) => {
  return (
    <button
      onClick={onClick}
      className={`service-card ${primary ? 'service-card-primary' : ''} ${isDarkMode ? 'dark' : ''}`}
    >
      <div className="service-card-content">
        <div className={`service-icon ${primary ? 'service-icon-primary' : ''}`}>
          {icon}
        </div>
        <div className="service-text">
          <h3 className="service-title">{title}</h3>
          {subtitle && <p className="service-subtitle">{subtitle}</p>}
        </div>
        <div className="service-arrow">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {primary && <div className="service-badge">Most Popular</div>}
    </button>
  );
};

const ServicesLanding = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('emergency-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('emergency-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const services = [
    {
      icon: '🚑',
      title: 'Ambulance Service',
      subtitle: 'Request an ambulance immediately',
      route: '/emergency/patient',
      primary: true
    },
    {
      icon: '🧴',
      title: 'Oxygen Cylinder',
      subtitle: 'Home oxygen delivery',
      route: '/emergency/oxygen-cylinder',
      primary: false
    },
    {
      icon: '👨‍⚕️',
      title: 'Emergency Doctor',
      subtitle: 'Instant consultation',
      route: '#',
      primary: false
    },
    {
      icon: '🧑‍⚕️',
      title: 'Nurse',
      subtitle: 'On-demand nursing',
      route: '/emergency/nurse-marketplace',
      primary: false
    },
    {
      icon: '🦽',
      title: 'Wheelchair',
      subtitle: 'Mobility assistance',
      route: '/emergency/wheelchair',
      primary: false
    }
  ];

  return (
    <div className={`services-landing ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Top Right Buttons */}
      <div className="top-right-buttons">
        <button
          onClick={toggleDarkMode}
          className="dark-mode-toggle"
          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDarkMode ? (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button 
          onClick={() => navigate('/dashboard')}
          className="back-button-top"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
      </div>

      {/* Header */}
      <header className="services-header">
        <div className="header-container">
          <div className="header-content">
            <div className="header-text">
              <h1 className="header-title">Emergency Services</h1>
              <p className="header-subtitle">Choose a service to get immediate assistance</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="services-main">
        <div className="services-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                subtitle={service.subtitle}
                onClick={() => service.route !== '#' && navigate(service.route)}
                primary={service.primary}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServicesLanding;
